import {
    amountBlockCl,
    amountBlockCli,
    personalInformation,
    personalInformationCoAplicant,
    secondBlock,
    thirdBlock,
    type SwedishLoanApplication,
    type SwedishApplicant,
    initialValues,
    SwedishLoanApplicationCleaned,
} from './fieldDefinitions';
import { cleanupSwedishNationalId } from '@n2-common/validation/se/nationalId';
import { AccomodationStatusValue, EmploymentStatusCode } from '@n2-common/uiFieldValues/applicationFormFields';
import { type TFunction } from 'i18next';
import { CliFieldConfig } from '../AmountVisualizer';
import { getConsentFieldDef } from '@n2-common/uiFieldValues/consents';
import { isGuid } from '@n2-common/lib';

export function getWizardStagesCl(t: TFunction) {
    return [
        amountBlockCl(t),
        personalInformation(t, false),
        secondBlock(t, 'mainApplicant'),
        thirdBlock(t, 'mainApplicant', false),

        personalInformationCoAplicant(t, false),
        secondBlock(t, 'coApplicant'),
        thirdBlock(t, 'coApplicant', false),
    ];
}

export function getWizardStagesCli(t: TFunction, config?: CliFieldConfig) {
    if (!config) {
        throw new Error('CliFieldConfig is required');
    }
    return [
        amountBlockCli(t, config),
        personalInformation(t, true),
        secondBlock(t, 'mainApplicant'),
        thirdBlock(t, 'mainApplicant', true),
        ...(config?.coApplicantName
            ? [personalInformationCoAplicant(t, true, config), secondBlock(t, 'coApplicant'), thirdBlock(t, 'coApplicant', true)]
            : []),
        {
            legend: 'Morrow Bank - Credit terms',
            sectionNameForTracking: 'confirm',
            fields: [
                getConsentFieldDef<SwedishLoanApplication>('MKT_1_PROFILING_1', t, 'SE', 'consents'),
                getConsentFieldDef<SwedishLoanApplication>('MKT_2_COOP_1', t, 'SE', 'consents'),
                getConsentFieldDef<SwedishLoanApplication>('MKT_3_COMM_1', t, 'SE', 'consents'),
                //getConsentFieldDef('ConfirmSA', t, 'SE'),
            ],
        },
    ];
}

export function cleanupFormValuesForSubmitting(values: SwedishLoanApplication): SwedishLoanApplication {
    return {
        ...values,
        mainApplicant: getCleanedApplicantDataWithNulledHiddenFields(values.mainApplicant),
        coApplicant: values.hasCoApplicant && values.coApplicant ? getCleanedApplicantDataWithNulledHiddenFields(values.coApplicant) : null,
    };
}
export function cleanupFormValuesForSubmittingCli(values: SwedishLoanApplication): SwedishLoanApplicationCleaned {
    values =
        values &&
        Object.entries(values).reduce((acc, [key, value]) => (key in initialValues ? { ...acc, [key]: value } : acc), {} as SwedishLoanApplication);
    if (!isGuid(values.coSignerGpid)) {
        delete values.coSignerGpid;
    } else console.log(values.coSignerGpid);
    return {
        ...values,
        mainApplicant: getCleanedApplicantDataWithNulledHiddenFields(values.mainApplicant, false),
        coApplicant: values.hasCoApplicant && values.coApplicant ? getCleanedApplicantDataWithNulledHiddenFields(values.coApplicant, false) : null,
        consents: Object.entries(values.consents).reduce((m, [key, value]) => (value ? [...m, key] : m), [] as string[]),
    };
}

function getCleanedApplicantDataWithNulledHiddenFields(applicant: SwedishApplicant, withSsn = true): SwedishApplicant {
    applicant =
        applicant &&
        Object.entries(applicant).reduce(
            (acc, [key, value]) => (key in initialValues.mainApplicant ? { ...acc, [key]: value } : acc),
            {} as SwedishApplicant
        );
    return (
        applicant && {
            ...applicant,
            accommodationTypeOther: specifyAccommodationTypeOther(applicant?.accommodationType as AccomodationStatusValue)
                ? applicant.accommodationTypeOther
                : '',
            nameOfEmployer: specifyEmployerName(applicant?.employmentStatus as EmploymentStatusCode) ? applicant.nameOfEmployer : '',
            accommodationCost: specifyAccomodationCost(applicant?.accommodationType as AccomodationStatusValue) ? applicant.accommodationCost : '',
            studentLoanMontlyCost: specifyStudenLoanPayment(applicant) ? applicant.studentLoanMontlyCost : '',
            nationalId: withSsn ? cleanupSwedishNationalId(applicant.nationalId) : '',
        }
    );
}

export function specifyAccommodationTypeOther(accommodationType: AccomodationStatusValue | '' | null | undefined) {
    if (!accommodationType || accommodationType === null) {
        return false;
    }
    return accommodationType === 'O';
}

export function specifyAccomodationCost(accommodationType: AccomodationStatusValue | '' | null | undefined) {
    if (accommodationType === '' || !accommodationType || accommodationType === null) {
        return false;
    }
    return accommodationType !== 'H';
}

export function specifyEmployerName(employmentStatus: EmploymentStatusCode | '' | null | undefined) {
    if (!employmentStatus || employmentStatus === null) {
        return false;
    }
    return employmentStatus === 'F' || employmentStatus === 'I';
}

function specifyStudenLoanPayment(applicant: SwedishApplicant | null | undefined) {
    if (!applicant || applicant === null) {
        return false;
    }
    return applicant.hasStudentLoan === 'true';
}
